"use client"

import * as React from "react"
import { Calendar as CalendarIcon, X } from "lucide-react"
import { type DateRange } from "react-day-picker"
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
  format,
  isSameDay,
} from "date-fns"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Separator } from "@/components/ui/separator"

// ── Preset types ──

export interface DateRangePreset {
  label: string
  getValue: () => DateRange
}

// ── Default presets ──

export const DEFAULT_PRESETS: DateRangePreset[] = [
  {
    label: "Today",
    getValue: () => ({
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = subDays(new Date(), 1)
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) }
    },
  },
  {
    label: "This Week",
    getValue: () => ({
      from: startOfWeek(new Date(), { weekStartsOn: 1 }),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: "Last Week",
    getValue: () => {
      const lastWeek = subWeeks(new Date(), 1)
      return {
        from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      }
    },
  },
  {
    label: "This Month",
    getValue: () => ({
      from: startOfMonth(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: "Last Month",
    getValue: () => {
      const lastMonth = subMonths(new Date(), 1)
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) }
    },
  },
  {
    label: "This Quarter",
    getValue: () => ({
      from: startOfQuarter(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: "Last Quarter",
    getValue: () => {
      const lastQ = subQuarters(new Date(), 1)
      return { from: startOfQuarter(lastQ), to: endOfQuarter(lastQ) }
    },
  },
]

// ── Date field option ──

export interface DateFieldOption {
  key: string
  label: string
}

// ── Props ──

interface DateRangePickerProps {
  /** Current date range */
  value: DateRange | undefined
  /** Called when range changes */
  onChange: (range: DateRange | undefined) => void
  /** Placeholder text. Default: "Pick a date range" */
  placeholder?: string
  /** Popover alignment. Default: "start" */
  align?: "start" | "center" | "end"
  /** Extra className for trigger button */
  className?: string
  /** Show preset sidebar. Default: true */
  showPresets?: boolean
  /** Custom presets (replaces defaults) */
  presets?: DateRangePreset[]
  /** Disable dates after today. Default: false */
  disableFutureDates?: boolean
  /** Number of calendar months. Default: 2 */
  numberOfMonths?: 1 | 2
  /** Optional date field tabs (e.g. Date Added, Hire Date, Modified Date) */
  dateFieldOptions?: DateFieldOption[]
  /** Currently selected date field key */
  activeDateField?: string
  /** Called when date field tab changes */
  onDateFieldChange?: (key: string) => void
}

// ── Helpers ──

/**
 * Compact label for the filter-chip trigger button.
 *
 * Goal: keep the trigger narrow (≤ ~22 chars) so it reads as a standard
 * filter chip, not a data cell. The year is dropped whenever it matches
 * the current year; same-month ranges collapse to `Apr 1–15`.
 *
 * The POPOVER still shows the full verbose range inside its body.
 *
 *   From  Apr 1              (same day, same year)
 *   Apr 3 – 15               (same month + year)
 *   Apr 28 – May 3           (same year, different months)
 *   Dec 28 2025 – Jan 4 2026 (cross-year — years shown)
 */
function formatDateRange(range: DateRange | undefined): string | null {
  if (!range?.from) return null
  const currentYear = new Date().getFullYear()
  if (!range.to) {
    const y = range.from.getFullYear() !== currentYear ? ", yyyy" : ""
    return `From ${format(range.from, `MMM d${y}`)}`
  }
  if (isSameDay(range.from, range.to)) {
    const y = range.from.getFullYear() !== currentYear ? ", yyyy" : ""
    return format(range.from, `MMM d${y}`)
  }
  const fromYear = range.from.getFullYear()
  const toYear = range.to.getFullYear()
  if (fromYear !== toYear) {
    return `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`
  }
  const ySuffix = fromYear !== currentYear ? `, ${fromYear}` : ""
  if (range.from.getMonth() === range.to.getMonth()) {
    return `${format(range.from, "MMM d")}–${format(range.to, "d")}${ySuffix}`
  }
  return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d")}${ySuffix}`
}

function isPresetActive(preset: DateRangePreset, value: DateRange | undefined): boolean {
  if (!value?.from || !value?.to) return false
  const presetValue = preset.getValue()
  if (!presetValue.from || !presetValue.to) return false
  return isSameDay(value.from, presetValue.from) && isSameDay(value.to, presetValue.to)
}

// ── Component ──

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Pick a date range",
  align = "start",
  className,
  showPresets = true,
  presets = DEFAULT_PRESETS,
  disableFutureDates = false,
  numberOfMonths = 1,
  dateFieldOptions,
  activeDateField,
  onDateFieldChange,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false)
  // Draft mirrors `value` while the popover is open so Cancel can revert.
  // Presets bypass the draft and commit immediately (that's the point of a
  // preset). Reset clears + commits + closes.
  const [draft, setDraft] = React.useState<DateRange | undefined>(value)

  // Sync draft whenever the popover opens OR the external value changes.
  React.useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  const label = formatDateRange(value)
  const isActive = !!value?.from
  const activeFieldLabel = dateFieldOptions?.find(o => o.key === activeDateField)?.label

  const handlePresetClick = (preset: DateRangePreset) => {
    onChange(preset.getValue())
    setOpen(false)
  }

  const handleRangeSelect = (range: DateRange | undefined) => {
    // Draft-only update — user clicks Apply to commit.
    setDraft(range)
  }

  const handleApply = () => {
    onChange(draft)
    setOpen(false)
  }

  const handleReset = () => {
    setDraft(undefined)
    onChange(undefined)
    setOpen(false)
  }

  const handleCancel = () => {
    // Revert any uncommitted draft edits, close without saving.
    setDraft(value)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            // Compact filter-chip shape. Hard max-width so cross-year ranges
            // (e.g. "Dec 28, 2025 – Jan 4, 2026") truncate with ellipsis instead
            // of stretching the toolbar. The full range stays readable in the
            // popover body + native title-tooltip.
            "h-9 max-w-60 justify-start gap-1.5 text-left font-normal border-dashed",
            isActive && "bg-secondary border-solid",
            className,
          )}
          title={
            label
              ? activeFieldLabel
                ? `${activeFieldLabel}: ${label}`
                : label
              : placeholder
          }
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
          {/* Filter title — always shown so the chip reads as a filter, not a
              data value. Matches the multi-select filter chip shape. */}
          <span className="shrink-0 font-medium">
            {activeFieldLabel ?? placeholder}
          </span>
          {label && (
            <>
              <span className="text-muted-foreground/50" aria-hidden>·</span>
              <span className="truncate text-muted-foreground">{label}</span>
            </>
          )}
          {isActive && (
            <span
              role="button"
              tabIndex={-1}
              title="Clear date range"
              className="ml-0.5 inline-flex shrink-0 items-center rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onPointerDown={(e) => {
                // Prevent Radix Popover trigger from receiving the event
                e.stopPropagation()
                e.preventDefault()
              }}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onChange(undefined)
                setOpen(false)
              }}
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align={align}
        // Wire the popover to the window via Radix Portal (default) and cap
        // its height so overflow scrolls inside the popover instead of being
        // clipped by ancestor containers. 520px fits two months + footer on
        // most viewports; 85vh is the hard ceiling for small screens.
        collisionPadding={12}
      >
        <div className="flex flex-col max-h-[min(85vh,540px)]">
          {/* Deprecated field-tabs header — only renders for legacy callers that
              still pass dateFieldOptions. New consumers should render one
              DateFieldFilter chip per field instead. See
              project_date_filter_chip_spec.md. */}
          {dateFieldOptions && dateFieldOptions.length > 1 && (
            <div className="flex items-center gap-1 border-b px-3 py-2 shrink-0">
              {dateFieldOptions.map((opt) => (
                <Button
                  key={opt.key}
                  variant={activeDateField === opt.key ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs px-3 rounded-full"
                  onClick={() => onDateFieldChange?.(opt.key)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          )}

          {/* Scrollable body — presets sidebar + calendar. Footer stays pinned
              so Apply/Cancel/Reset are always reachable. */}
          <div className="flex overflow-y-auto">
            {showPresets && (
              <>
                <div className="flex flex-col gap-0.5 p-2 w-36 shrink-0">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground px-2 py-1">
                    Quick Select
                  </p>
                  {presets.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "justify-start h-7 px-2 text-xs font-normal",
                        isPresetActive(preset, draft) && "bg-accent font-medium",
                      )}
                      onClick={() => handlePresetClick(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
                <Separator orientation="vertical" />
              </>
            )}
            <div className="p-2">
              <Calendar
                mode="range"
                selected={draft}
                onSelect={handleRangeSelect}
                numberOfMonths={numberOfMonths}
                disabled={disableFutureDates ? { after: new Date() } : undefined}
                initialFocus
              />
            </div>
          </div>

          {/* Footer — three buttons: Reset | Cancel | Apply. Reset + Cancel on
              the left as ghost/outline, Apply on the right as primary. Reset
              is disabled when nothing is committed AND the draft is empty. */}
          <div className="flex items-center justify-between gap-2 border-t px-3 py-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-muted-foreground"
              onClick={handleReset}
              disabled={!isActive && !draft?.from}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Reset
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={handleApply}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
